<template>
  <div class="relative" :class="[ padding ]" ref="$container">
    <div :style="{ height: railHeight + 'px' }" />

    <div class="absolute top-0 left-0 w-full h-full overflow-auto md:overflow-hidden no-scrollbar" ref="$scroller">

      <div
        class="absolute top-0 left-0 transition duration-400 ease-in-out"
        :class="[ padding ]"
        :style="{ transform: `translateX(-${position * moveSize}px)` }"
        ref="$rail"
      >
        <div
          class="flex items-start md:pr-0"
          :class="[ maxMoves > 0 ? 'pr-40' : '', spacing ]"
        >
          <slot />
        </div>
      </div>
    </div>

    <div
      class="hidden md:flex absolute z-20 left-0 top-0 h-full items-center px-4 bg-gradient-to-l from-transparent transition"
      :class="[
        gradient,
        position > 0 ? '' : 'opacity-0 pointer-events-none'
      ]"
      :style="theme?.base ? {
        '--tw-gradient-to': theme.base.background[themeWeak ? 'weak' : 'base'],
        '--tw-gradient-from': theme.base.background[themeWeak ? 'weak' : 'base'] + '00',
        '--tw-gradient-stops': 'var(--tw-gradient-from), var(--tw-gradient-to)'
      } : {}"
    >
      <ButtonBase style-type="round" color="white" size="xs" @click.stop.prevent="position--">
        <IconBase name="cute/angle-right" class="-scale-x-100" :width="18" />
      </ButtonBase>
    </div>

    <div
      class="flex absolute z-20 right-0 top-0 h-full items-center pl-6 pr-4 bg-gradient-to-r from-transparent transition pointer-events-none"
      :class="[
        gradient,
        position < maxMoves ? 'md:pointer-events-auto' : 'opacity-0'
      ]"
      :style="theme?.base ? {
        '--tw-gradient-to': theme.base.background[themeWeak ? 'weak' : 'base'],
        '--tw-gradient-from': theme.base.background[themeWeak ? 'weak' : 'base'] + '00',
        '--tw-gradient-stops': 'var(--tw-gradient-from), var(--tw-gradient-to)'
      } : {}"
    >
      <ButtonBase style-type="round" class="hidden md:flex" color="white" size="xs" @click.stop.prevent="position++">
        <IconBase name="cute/angle-right" :width="18" />
      </ButtonBase>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Theme } from '@/classes'

const props = withDefaults(defineProps<{
  gradient?: string
  spacing?: string
  padding?: string
  moveSize?: number
  theme?: Theme | null
  themeWeak?: boolean
}>(), {
  gradient: 'from-charcoal-950/0 to-charcoal-950/50',
  spacing: 'space-x-3',
  padding: 'p-4 md:p-6',
  theme: null,
  themeWeak: false,
  moveSize: 400
})

const emit = defineEmits<{
  (e: 'scroll-start'): void
  (e: 'scroll-end'): void
}>()

const { watchersAdd, watchersRestart, watchersStop } = useWatchers()

useKeepAlive({
  onActivated: () => {
    watchersRestart()
  },
  onLeave: () => {
    watchersStop()
  }
})

const $container = ref()
const $rail = ref()
const $scroller = ref()

const { width: containerWidth } = useElementSize($container)
const { width: railWidth, height: railHeight } = useElementSize($rail)
const { x, isScrolling } = useScroll($scroller)

watchersAdd(() => watch(isScrolling, (value) => {
  if (value) {
    emit('scroll-start')
  } else {
    emit('scroll-end')
  }
}))

const position = ref(0)

const maxMoves = computed(() => {
  return Math.ceil((railWidth.value - containerWidth.value) / props.moveSize)
})

defineExpose({
  resetScroll: () => {
    x.value = 0
  }
})

</script>